import { useEffect, useRef } from 'react'

import { useAppSelector } from '@/redux/hooks'
import {
  selectVideoWallCameraCount,
  selectVideoWallIsOpen,
} from '@/redux/videoWall/videoWallSlice'
import { startTimer, trackEvent } from '@/utils/analytics'

import { VIDEO_WALL_ANALYTICS } from '../constants/constants'

/**
 * Fires the video wall open/close analytics. Intended to be called from the
 * always-mounted VideoWallOverlay so it observes every open/close path (the "W"
 * shortcut, Escape, and the close button).
 *
 * Open/close is derived from an `isOpen` transition (prevRef) rather than a
 * component mount/unmount, which makes it idempotent under React re-renders /
 * StrictMode double-invocation. Add/remove events fire in their click handlers.
 *
 * `cameraCountOverride` is for walls whose cameras don't live in the redux
 * `cells` state (the seeded Sales wall) — without it those opens would report
 * camera_count 0.
 */
export const useVideoWallAnalytics = (cameraCountOverride?: number): void => {
  const isOpen = useAppSelector(selectVideoWallIsOpen)
  const cameraCount = useAppSelector(selectVideoWallCameraCount)

  const wasOpenRef = useRef(isOpen)
  // Read the latest count inside the transition effect without making it a
  // dependency — we only fire on isOpen transitions.
  const cameraCountRef = useRef(cameraCount)
  cameraCountRef.current = cameraCountOverride ?? cameraCount

  useEffect(() => {
    if (isOpen === wasOpenRef.current) {
      return
    }
    wasOpenRef.current = isOpen

    if (isOpen) {
      trackEvent(VIDEO_WALL_ANALYTICS.opened, {
        camera_count: cameraCountRef.current,
      })
      // Pair with trackEvent(closed) below so Mixpanel attaches a Duration
      // measuring how long the wall stayed open.
      startTimer(VIDEO_WALL_ANALYTICS.closed)
    } else {
      trackEvent(VIDEO_WALL_ANALYTICS.closed, {
        camera_count: cameraCountRef.current,
      })
    }
  }, [isOpen])
}
